/*
  사원증 결제정보 엑셀파일로 관리 (사원증 결제 관리 > 엑셀파일로 관리)

  다운로드 → 파일 수정 → 업로드 → 미리보기 → 저장하기 흐름.
  드래그앤드롭 박스는 components/Commons/ExcelDropzone 재사용.
*/
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import download from 'downloadjs';
import moment from 'moment-timezone';
import { Button } from 'semantic-ui-react';

import {
  downloadExcelFile,
  getExcelFileName,
  getExcelFileStatus,
  isExcelReady,
  requestEmployeeCardPaymentExcel,
  saveEmployeeCardPayment
} from 'apis/employeeCardPayment';
import { ExcelDropzone, LoadingBar } from 'components/Commons';
import { hasEmployeeCardPaymentAuth, isEmployeeCardPaymentAllowedCompany } from 'constants/employeeCardPayment';
import cm from 'helpers/commons';
import storage from 'helpers/storage';
import toastr from 'helpers/toastr';
import { safeHistoryPush } from 'helpers/pathValidator';
import { delay } from 'utils';

import ExcelDownloadMoveModal from './ExcelDownloadMoveModal';
import { readEmployeeCardPaymentExcel } from './excelParser';
import writeEmployeeCardPaymentResultExcel from './resultExcel';

const EXCEL_DOWNLOAD_PAGE_URL = '/service/finance/excel-download';

// 생성이 끝났는지 1초마다 확인하고, 10번(10초)까지 기다려도 안 끝나면 이동 모달로 넘긴다.
const EXCEL_STATUS_CHECK_INTERVAL = 1000;
const EXCEL_STATUS_CHECK_COUNT = 10;

/* 준비되면 그 상태를, 끝까지 생성 중이면 null 을 돌려준다. */
const waitForExcelReady = async (fileId) => {
  for (let i = 0; i < EXCEL_STATUS_CHECK_COUNT; i += 1) {
    // 한 번에 몰아 보내면 의미가 없다. 간격을 두고 차례로 확인한다.
    // eslint-disable-next-line no-await-in-loop
    await delay(EXCEL_STATUS_CHECK_INTERVAL);
    // eslint-disable-next-line no-await-in-loop
    const status = await getExcelFileStatus(fileId);

    if (isExcelReady(status)) return status;
  }

  return null;
};

const DEFAULT_EXCEL_FILE_NAME = '사원증결제정보.xlsx';

/*
  드래그앤드롭은 input 의 accept 를 거치지 않아 어떤 파일이든 들어온다.
  파일 선택 창과 같은 기준으로 걸러내려고 확장자를 한 곳에서 관리한다.
*/
const EXCEL_FILE_EXTENSIONS = ['.xlsx', '.xls'];
const EXCEL_FILE_ACCEPT = EXCEL_FILE_EXTENSIONS.join(',');

const isExcelFile = (file) => {
  const name = (file && file.name ? file.name : '').toLowerCase();

  return EXCEL_FILE_EXTENSIONS.some((extension) => name.slice(-extension.length) === extension);
};

/*
  로딩창을 켠 직후 바로 무거운 작업을 시작하면 브라우저가 그것을 그릴 틈이 없다.
  파싱이 메인 스레드를 수 초간 잡는 큰 파일에서 로딩창이 간헐적으로 안 보이던 원인이다.
  화면에 한 번 그려진(= 두 번째 프레임이 온) 뒤에 시작하도록 한 프레임 양보한다.
  백그라운드 탭처럼 프레임이 오지 않는 경우를 대비해 타이머로도 풀어준다.
*/
const PAINT_WAIT_TIMEOUT = 100;

const waitForPaint = () =>
  new Promise((resolve) => {
    const timer = window.setTimeout(resolve, PAINT_WAIT_TIMEOUT);

    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => {
        window.clearTimeout(timer);
        resolve();
      })
    );
  });

const FAILED_RESULT_FILE_TITLE = '사원증_결제정보_실패_목록';

// 헤더가 없거나 형식이 다르면 기본 이름을 쓴다.
const getFileName = (headers) => {
  const disposition = (headers && headers['content-disposition']) || '';
  const matched = disposition.split('=')[1];

  if (!matched) return DEFAULT_EXCEL_FILE_NAME;

  try {
    return decodeURIComponent(matched.trim().replace(/["']/g, '')) || DEFAULT_EXCEL_FILE_NAME;
  } catch (error) {
    return DEFAULT_EXCEL_FILE_NAME;
  }
};

/*
  업로드한 값으로 무엇을 하게 될지 미리보기에 보여준다.
  검증에 걸린 행은 전송하지 않으므로 다른 판정보다 먼저 본다.
*/
const getPlannedAction = (row) => {
  if (row.isValid === false) return { text: '확인필요', isWarn: true };
  if (row.currentBarCode === row.barCode) return { text: '변경없음', isWarn: false };
  if (!row.barCode) return { text: '삭제', isWarn: true };
  if (row.currentBarCode) return { text: '수정', isWarn: false };
  return { text: '등록', isWarn: false };
};

// 전송 전 요약에 세는 처리 구분. '변경없음' 은 전송 대상이 아니라 세지 않는다.
const PLANNED_SUMMARY_LABELS = ['등록', '수정', '삭제', '확인필요'];

const renderSummaryCount = (label, count) => (
  <span key={label} className="count">
    {label} ({cm.numberComma(count)})
  </span>
);

// 전송한 뒤 실패한 행과 전송을 건너뛴(확인필요) 행을 같게 다룬다.
const isFailedRow = (row) => row.isSuccess === false || row.isValid === false;

const getResultClassName = (row) => {
  if (row.isSuccess === true) return 'preview-result success';
  if (isFailedRow(row) || row.resultText) return 'preview-result fail';
  return null;
};

const PREVIEW_COLUMNS = [
  { header: '#', name: 'index', width: 60, textAlign: 'center' },
  { header: 'ID', name: 'signId', width: 160 },
  { header: '이름', name: 'name', width: 140 },
  { header: '현재 사원증 결제정보', name: 'currentBarCode', width: 200 },
  { header: '변경할 사원증 결제정보', name: 'barCode', width: 200 },
  { header: '처리 구분', name: 'plannedAction', width: 100 },
  { header: '저장 결과', name: 'resultText', width: 120 },
  { header: '비고', name: 'message' }
];

const Wrapper = styled.main`
  padding-bottom: 12px;

  .download-button {
    width: 260px;
    height: 33px;
    margin: 0px;
    padding: 0px;
    line-height: 26px;
  }

  /* 안내문은 파일 선택 박스 아래에 둔다 */
  .guide-text {
    margin-top: 12px;
    color: #8e8e8e;
    font-size: 14px;
    line-height: 1.6;
  }

  .guide-text p {
    /* ※ 다음 줄이 기호 뒤에 맞춰 들여써지도록 */
    margin: 0;
    padding-left: 16px;
    text-indent: -16px;
  }

  .guide-text p + p {
    margin-top: 4px;
  }

  /* 위 여백은 업로드 전(파일 선택 박스)과 같아야 머리글 구분선과의 간격이 바뀌지 않는다 */
  .preview-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin: 0px 0px 8px;
  }

  .preview-file-label {
    margin-left: 12px;
    color: #000000;
    font-size: 14px;
  }

  /* 라벨과 초기화 버튼이 밀리지 않도록 파일명에만 말줄임을 건다 */
  .preview-file {
    max-width: 320px;
    margin-left: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #000000;
    font-size: 14px;
    font-weight: 700;
  }

  .preview-reset {
    margin-left: 8px;
    padding: 4px 10px;
    border: 1px solid #d7d7d7;
    border-radius: 4px;
    background: #ffffff;
    color: #000000;
    font-size: 14px;
    cursor: pointer;
  }

  /* 항목마다 색·굵기를 달리하지 않고 한 줄 전체를 같은 글꼴로 읽히게 둔다 */
  .preview-summary {
    color: #000000;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
  }

  .preview-summary .total {
    margin-right: 6px;
  }

  .preview-summary .count + .count::before {
    content: '/';
    margin: 0px 6px;
  }

  /*
    페이지를 나누지 않고 전부 그린다. 세로는 높이를 제한하지 않아 페이지 스크롤로 내려가고,
    가로는 좁은 창에서 표가 넘칠 때만 이 영역에서 스크롤한다.
  */
  .preview-box {
    border: 1px solid #d7d7d7;
    border-radius: 4px;
    overflow-x: auto;
  }

  .preview-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }

  .preview-table thead {
    background: #fafafa;
  }

  .preview-table th,
  .preview-table td {
    padding: 8px;
    border-bottom: 1px solid #d7d7d7;
    text-align: left;
    color: #353c49;
    white-space: nowrap;
  }

  .preview-table th {
    font-weight: 700;
  }

  .preview-table tbody tr:last-child td {
    border-bottom: 0;
  }

  /* 색으로 구분되므로 굵기는 다른 열과 같게 둔다 */
  .preview-result.success {
    color: #3ea856;
  }

  .preview-result.fail {
    color: #ff0000;
  }

  .preview-action.warn {
    color: #ff0000;
  }

  .preview-index {
    color: #8e8e8e;
    text-align: center;
  }

  .preview-message {
    color: #8e8e8e;
    white-space: normal;
  }

  /* 저장 결과가 실패인 행은 사유도 같은 색으로 읽히게 한다 */
  .preview-message.fail {
    color: #ff0000;
  }
`;

/*
  저장하기는 표 길이와 무관하게 늘 닿을 수 있도록 표 아래에 붙여 띄운다.
  sticky 라 폭이 표(.preview-box)와 자동으로 같아진다.
  IE11 은 sticky 를 무시해 표 끝에 그대로 놓이지만 누르는 데는 지장이 없다.
*/
const SaveBox = styled.div`
  position: sticky;
  /*
    스크롤 영역이 좌우·아래로 20px 패딩을 갖고 있어 bottom: 0 이면 그만큼 떠 보인다.
    음수로 당겨 화면 아래에 더 가깝게 붙인다.
  */
  bottom: -8px;
  z-index: 20;
  margin-top: 12px;
  padding: 12px 16px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.2);
  text-align: right;

  /* 상단 '사원증 결제정보 엑셀 다운로드' 버튼과 같은 모양으로 맞춘다 */
  .ui.button {
    height: 33px;
    margin: 0px;
    padding: 0px;
    line-height: 26px;
  }

  .ui.button + .ui.button {
    margin-left: 8px;
  }

  /*
    semantic 기본 비활성화는 opacity 만 낮춰 초록 글자·테두리가 그대로 비친다.
    누를 수 없다는 게 분명히 보이도록 회색으로 덮는다.
    .ui.inverted.green.button 이 색을 !important 로 걸어둬서, 선택자를 겹쳐(&&&) 우선순위를 확실히 올린다.
  */
  &&& .ui.button:disabled,
  &&& .ui.button.disabled {
    opacity: 1 !important;
    background-color: #e5e5e5 !important;
    color: #767676 !important;
    box-shadow: none !important;
  }

  .failed-button {
    width: 180px;
  }

  .save-button {
    width: 140px;
  }
`;

/*
  LoadingBar(Dimmer) 위에 얹는 진행 표시와 중단 버튼.
  LoadingBar 는 children 을 componentWillMount 에서 한 번만 잡아둬 갱신되지 않으므로
  진행 숫자는 이 요소로 따로 그린다.
*/
const ProgressBox = styled.div`
  position: fixed;
  top: 50%;
  left: 0;
  right: 0;
  z-index: 10001;
  margin-top: 56px;
  text-align: center;

  .progress-text {
    color: #ffffff;
    font-size: 14px;
    line-height: 1.5;
  }

  .progress-cancel {
    margin-top: 12px;
    padding: 6px 20px;
    border: 1px solid #ffffff;
    border-radius: 4px;
    background: transparent;
    color: #ffffff;
    font-size: 14px;
    cursor: pointer;
  }
`;

const EmployeeCardPaymentByExcel = ({ history }) => {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [savedCount, setSavedCount] = useState(0);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  // 저장 루프 도중 중단 요청. 루프가 매 건 시작 전에 확인한다.
  const isCancelledRef = useRef(false);

  const isBlocking = isParsing || isSaving || isDownloading;

  // 목록 화면과 같은 화이트리스트로 진입을 막는다(URL 직접 접근 대비).
  const isAllowed = useMemo(() => {
    const company = storage.get('company') || {};
    const info = storage.get('info') || {};

    // LNB 에 안 보이는 사용자가 URL 로 들어오는 것까지 막는다(메뉴와 같은 권한 코드).
    return isEmployeeCardPaymentAllowedCompany(company.id) && hasEmployeeCardPaymentAuth(info.auth && info.auth.auth);
  }, []);

  useEffect(() => {
    if (!isAllowed) {
      safeHistoryPush(history, '/main/dashBoard');
      return;
    }

    cm.mainTitle('사원증 결제정보 엑셀파일로 관리');
  }, []);

  const handleFileSelect = async (selected) => {
    // 엑셀이 아니면 읽어보지 않고 바로 돌려보낸다(용량이 큰 파일을 붙잡고 있지 않도록).
    if (!isExcelFile(selected)) {
      toastr('top-right', 'error', `엑셀 파일(${EXCEL_FILE_ACCEPT})만 올릴 수 있습니다.`);
      return;
    }

    setFile(selected);
    setRows([]);
    setSummary(null);
    setIsParsing(true);

    try {
      // 로딩창이 실제로 그려진 뒤에 파싱을 시작한다(아래 waitForPaint 주석 참고)
      await waitForPaint();

      const parsed = await readEmployeeCardPaymentExcel(selected);

      /*
        보낼 행이 없으면 올린 의미가 없다. 양식 오류와 똑같이 업로드 전 상태로 되돌린다
        (파일만 올라간 채 표가 비어 있으면 무엇이 잘못됐는지 알기 어렵다).
      */
      if (!parsed.length) {
        setFile(null);
        toastr('top-right', 'error', '변경된 사원증 결제정보가 없습니다.');
        return;
      }

      setRows(parsed);
    } catch (error) {
      console.log('###### readEmployeeCardPaymentExcel : ', error);
      setFile(null);
      setRows([]);
      // 양식이 어긋난 경우는 무엇이 없는지 그대로 알려준다.
      toastr(
        'top-right',
        'error',
        error && error.isFormatError ? error.message : '엑셀 파일을 읽지 못했습니다. 파일 형식을 확인해 주세요.'
      );
    } finally {
      setIsParsing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setRows([]);
    setSummary(null);
  };

  const invalidCount = useMemo(() => rows.filter((row) => row.isValid === false).length, [rows]);

  const plannedCounts = useMemo(() => {
    const counts = {};

    rows.forEach((row) => {
      const { text } = getPlannedAction(row);

      counts[text] = (counts[text] || 0) + 1;
    });

    return counts;
  }, [rows]);

  // '#' 은 엑셀 값을 그대로 쓴다. 그 열이 없거나 비어 있으면 번호를 지어내지 않고 빈 값으로 둔다.
  const previewRows = useMemo(() => rows.map((row) => ({ ...row, no: row.excelIndex || '-' })), [rows]);

  /*
    저장되지 않은 행 = 실패 + 확인필요(전송 자체를 건너뛴 행).
    둘 다 고쳐서 다시 올려야 하는 대상이라 '실패 전송 결과' 파일에 함께 담는다.
  */
  const unsavedRows = useMemo(() => rows.filter((row) => row.isSuccess !== true), [rows]);

  // 실패 목록은 고쳐서 다시 올릴 수 있는 양식이라 서버를 거치지 않고 화면에서 바로 만든다.
  const handleDownloadFailedResult = async () => {
    if (!unsavedRows.length) {
      toastr('top-right', 'warning', '실패 건이 없습니다.');
      return;
    }

    // 같은 화면에서 여러 번 받을 수 있어 파일명에 받은 시각을 붙인다. 메타정보 탭에도 같은 값을 적는다.
    const downloadedAt = moment();
    const fileName = `${FAILED_RESULT_FILE_TITLE}_${downloadedAt.format('YYYYMMDD_HHmmss')}.xlsx`;

    try {
      const buffer = await writeEmployeeCardPaymentResultExcel(unsavedRows, {
        fileName,
        downloadedAt: downloadedAt.format('YYYY-MM-DD HH:mm:ss')
      });

      download(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
    } catch (error) {
      console.log('###### writeEmployeeCardPaymentResultExcel : ', error);
      toastr('top-right', 'warning', '실패 목록 엑셀을 만들지 못했습니다.');
    }
  };

  /*
    엑셀은 비동기로 생성된다. 생성 요청 → 1초 간격으로 최대 10번 상태 확인 →
    준비됐으면 내려받고, 그때까지 생성 중이면 다운로드 페이지 이동 모달을 띄운다.
  */
  const handleDownload = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const { fileId, fileName } = await requestEmployeeCardPaymentExcel();

      const status = fileId ? await waitForExcelReady(fileId) : null;

      if (!status) {
        setIsRequestModalOpen(true);
        return;
      }

      // 내려받기 쿼리에 파일명이 필요하다. 생성·상태 응답에 없으면 엑셀 내역 목록에서 찾는다.
      const name = fileName || (status && status.name) || (await getExcelFileName(fileId));

      // 이름을 못 찾으면 500 이 나므로, 받을 수 있는 다운로드 페이지로 안내한다.
      if (!name) {
        setIsRequestModalOpen(true);
        return;
      }

      const response = await downloadExcelFile(fileId, name);

      download(response.data, getFileName(response.headers));
      toastr('top-right', 'success', '엑셀 다운로드가 완료되었습니다.');
    } catch (error) {
      console.log('###### employeeCardPaymentExcelDownload : ', error);
      // 생성 요청 자체가 실패한 게 아니라면 다운로드 페이지에서 받을 수 있으므로 모달로 안내한다.
      setIsRequestModalOpen(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleMoveExcelDownloadPage = () => safeHistoryPush(history, EXCEL_DOWNLOAD_PAGE_URL);

  const handleCancelSave = () => {
    isCancelledRef.current = true;
  };

  const handleSave = async () => {
    if (!file || !rows.length) {
      toastr('top-right', 'warning', '업로드할 엑셀 파일을 선택해 주세요.');
      return;
    }
    if (isSaving) return;

    // 전송 전 검증에 걸린 행(비고 있음)은 보내지 않고 건너뛴다.
    if (invalidCount === rows.length) {
      toastr('top-right', 'warning', '저장할 수 있는 행이 없습니다. 비고를 확인해 주세요.');
      return;
    }

    setSavedCount(0);
    isCancelledRef.current = false;
    setIsSaving(true);

    // 일괄 업로드 API 가 따로 없어 사용자별로 PUT/DELETE 를 보낸다.
    // 서버 부하를 고려해 순차 호출하고, 건별 결과를 그대로 표에 채운다.
    const results = [];

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];

      // 중단하면 남은 행은 전송하지 못한 것이므로 실패로 남긴다.
      // 원래 보낼 대상이 아니던 확인필요 행은 중단 사유 대신 제 사유를 그대로 둔다.
      if (isCancelledRef.current) {
        rows.slice(i).forEach((remaining) => {
          if (remaining.isValid === false) {
            results.push({ ...remaining, resultText: '실패' });
            return;
          }

          results.push({
            ...remaining,
            isSuccess: false,
            resultText: '실패',
            message: '저장이 중단되었습니다.'
          });
        });
        break;
      }

      if (row.isValid === false) {
        // 전송을 건너뛴 행도 저장되지 않은 것은 같아 실패로 묶는다(비고에 사유가 남는다).
        results.push({ ...row, resultText: '실패' });
      } else {
        try {
          // eslint-disable-next-line no-await-in-loop
          await saveEmployeeCardPayment(row.userId, row.barCode);
          results.push({ ...row, isSuccess: true, resultText: '성공', message: '' });
        } catch (error) {
          const { response } = error || {};
          results.push({
            ...row,
            isSuccess: false,
            resultText: '실패',
            message: response && response.data ? response.data.message : '저장에 실패했습니다.'
          });
        }
      }

      setSavedCount(i + 1);
    }

    const success = results.filter((row) => row.isSuccess === true).length;
    const fail = results.filter((row) => row.isSuccess === false).length;
    const hold = results.filter((row) => row.isValid === false).length;

    const wasCancelled = isCancelledRef.current;

    setRows(results);
    setSummary({ total: results.length, success, fail, hold });
    setIsSaving(false);

    const counts = `성공 ${cm.numberComma(success)}건 / 실패 ${cm.numberComma(fail + hold)}건`;

    if (wasCancelled) {
      toastr('top-right', 'warning', `저장을 중단했습니다. ${counts}`);
      return;
    }

    toastr('top-right', fail || hold ? 'warning' : 'success', `총 ${cm.numberComma(results.length)}건 중 ${counts}`);
  };

  if (!isAllowed) return null;

  return (
    <Wrapper>
      {/*
        파싱·저장·엑셀 생성 대기 중에는 페이지 전체를 덮어 클릭을 막는다.
        style 을 넘기면 LoadingBar 의 기본값을 덮어쓰므로 배경·z-index 를 함께 준다.
      */}
      {isBlocking ? (
        <LoadingBar style={{ position: 'fixed', backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 10000 }} />
      ) : null}
      {isSaving ? (
        <ProgressBox>
          <div className="progress-text">
            {cm.numberComma(savedCount)} / {cm.numberComma(rows.length)} 건 처리 중
          </div>
          <button type="button" className="progress-cancel" onClick={handleCancelSave}>
            중단
          </button>
        </ProgressBox>
      ) : null}

      {/* 페이지 이름은 Main 레이아웃(.main-title)이 좌측에 렌더하고, .top-btn 이 그 우측에 붙는다 */}
      <div className="top-btn button">
        <Button inverted color="green" className="download-button" onClick={handleDownload} disabled={isDownloading}>
          {isDownloading ? '요청 중...' : '사원증 결제정보 엑셀 다운로드'}
        </Button>
      </div>

      {/* 업로드 전에는 파일 선택과 안내문만, 업로드 후에는 표만 보여준다 */}
      {rows.length === 0 ? (
        <div>
          <ExcelDropzone
            file={file}
            accept={EXCEL_FILE_ACCEPT}
            onFileSelect={handleFileSelect}
            onReset={handleReset}
          />

          <div className="guide-text">
            <p>※ 사원증 결제정보 엑셀 다운로드 후 [변경할 사원증 결제정보] 열을 수정할 수 있습니다.</p>
            <p>※ 파일 업로드 후 목록에서 수정될 내용을 확인하고 [저장하기]까지 눌러야 반영됩니다.</p>
            <p>※ 저장에 실패한 건은 [실패 건 엑셀 다운로드]로 받아 수정 후 다시 업로드할 수 있습니다.</p>
          </div>
        </div>
      ) : (
        <div>
          <div className="preview-header">
            {/* 전송 전에는 처리 구분별로, 전송 후에는 결과로 센다 */}
            <div className="preview-summary">
              <span className="total">총 ({cm.numberComma(rows.length)}) :</span>
              {summary
                ? [
                    renderSummaryCount('성공', summary.success),
                    // 확인필요는 전송하지 못한 건이라 실패에 합쳐 센다(실패 목록 엑셀에 담기는 범위와 같다).
                    renderSummaryCount('실패', summary.fail + summary.hold)
                  ]
                : PLANNED_SUMMARY_LABELS.map((label) => renderSummaryCount(label, plannedCounts[label] || 0))}
            </div>
            <span className="preview-file-label">|</span>
            <span className="preview-file-label"> 업로드</span>
            <span className="preview-file" title={file ? file.name : undefined}>
              {file ? file.name : ''}
            </span>
            <button type="button" className="preview-reset" onClick={handleReset}>
              초기화
            </button>
          </div>

          <div className="preview-box">
            <table className="preview-table">
              <thead>
                <tr>
                  {PREVIEW_COLUMNS.map((column) => (
                    <th key={column.name} style={{ width: column.width, textAlign: column.textAlign }}>
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row) => {
                  const planned = getPlannedAction(row);

                  return (
                    <tr key={row.rowNumber}>
                      <td className="preview-index">{row.no}</td>
                      <td>{row.signId || '-'}</td>
                      <td>{row.name || '-'}</td>
                      <td>{row.currentBarCode || '-'}</td>
                      <td>{row.barCode || '-'}</td>
                      <td className={planned.isWarn ? 'preview-action warn' : 'preview-action'}>{planned.text}</td>
                      <td className={getResultClassName(row)}>{row.resultText || '-'}</td>
                      <td className={isFailedRow(row) ? 'preview-message fail' : 'preview-message'}>
                        {row.message || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <SaveBox>
            {/* 저장을 눌러 결과가 나온 뒤에만 보인다 */}
            {summary ? (
              <Button
                inverted
                color="green"
                className="failed-button"
                content="실패 건 엑셀 다운로드"
                onClick={handleDownloadFailedResult}
                disabled={!unsavedRows.length}
              />
            ) : null}
            <Button
              inverted
              color="green"
              className="save-button"
              content={isSaving ? '저장 중...' : '저장하기'}
              onClick={handleSave}
              // 저장이 끝난 파일을 다시 누르면 성공했던 행까지 또 전송된다. 새 파일을 올려야 다시 저장할 수 있다.
              disabled={isSaving || !!summary}
            />
          </SaveBox>
        </div>
      )}

      <ExcelDownloadMoveModal
        open={isRequestModalOpen}
        close={() => setIsRequestModalOpen(false)}
        onMove={handleMoveExcelDownloadPage}
      />
    </Wrapper>
  );
};

export default EmployeeCardPaymentByExcel;
